'use client'

import { useMemo } from 'react'
import { useRouter, usePathname } from '@/i18n/navigation'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { PAGINATION_SIZE_OPTIONS } from '@/constants'

type PropsType = {
  currentSize: number
  // When provided, changes are reported via onChange instead of navigating (client-side browser).
  onChange?: (size: number) => void
}

const PageSizeSelector = ({ currentSize, onChange }: PropsType) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const t = useTranslations()

  // Memoize the current value to prevent re-renders
  const currentValue = useMemo(() => currentSize.toString(), [currentSize])

  const handleSizeChange = (value: string) => {
    if (onChange) {
      onChange(Number(value))
      return
    }

    const params = new URLSearchParams(searchParams.toString())
    params.set('size', value)
    params.delete('page') // Reset to page 1 when changing size
    router.push(`${pathname}?${params.toString()}`)
  }

  return (
    <div className="flex items-center gap-2">
      <label htmlFor="page-size" className="text-muted-foreground text-right text-xs">
        {t('items_per_page')}:
      </label>
      <Select key={currentValue} value={currentValue} onValueChange={handleSizeChange}>
        <SelectTrigger id="page-size">
          <SelectValue>{currentValue}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {PAGINATION_SIZE_OPTIONS.map((size, key) => (
            <SelectItem key={key} value={size.toString()}>
              {size}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export default PageSizeSelector
